import cn from 'classnames';
import { isFunction } from 'lodash';
import React from 'react';
import { isVisibleError } from '../../utils/isRealError';
import { ErrorHint } from '../ErrorHint';
import { QuestionDescription } from '../QuestionDescription';
import { QuestionTitle } from '../QuestionTitle';
import styles from './GenericQuestionHeader.module.css';

interface GenericQuestionHeaderProps {
  question: { text: string; description?: string; requiresAnswer?: boolean };
  groupId?: string;
  alignment: 'left' | 'center';
  questionId: string;
  errorMessage: React.ReactNode;
  gap?: 'xxs' | 'tiny';
  marginBottom?: 's' | 'xs';
  children?: React.ReactNode | ((props: { isError: boolean }) => React.ReactNode);
}

export const GenericQuestionHeader: React.FC<GenericQuestionHeaderProps> = ({
  question,
  alignment,
  questionId,
  errorMessage,
  gap = 'tiny',
  marginBottom = 's',
  children,
}) => {
  const isError = isVisibleError(errorMessage);
  const text = question.text.trim();
  const description = question.description?.trim() ?? '';

  const showHeader = !!text || !!question.description || !!errorMessage || !!children;

  if (!showHeader) return null;

  return (
    <header
      className={cn(styles.header, {
        [c(styles.marginBottomS)]: marginBottom === 's',
        [c(styles.marginBottomXS)]: marginBottom === 'xs',
        [c(styles.gapXXS)]: gap === 'xxs',
        [c(styles.gapTiny)]: gap === 'tiny',
      })}
    >
      <QuestionTitle required={question.requiresAnswer} questionId={questionId} alignment={alignment}>
        {text}
      </QuestionTitle>

      <QuestionDescription alignment={alignment}>{description}</QuestionDescription>

      <ErrorHint>{errorMessage}</ErrorHint>

      {isFunction(children) ? children({ isError }) : children}
    </header>
  );
};
